import "./styles.css";
import "./ui/heatbar.css";
import { Camera } from "./camera/camera";
import { VisionEngine } from "./vision/faceLandmarker";
import { Tracker } from "./vision/tracker";
import { AnimalEngine } from "./vision/animals";
import { AnimalTracker } from "./vision/animalTracker";
import { estimateGeometry } from "./vision/geometry";
import { loadHeatFactors, scoreHeat } from "./vision/heat";
import { Overlay } from "./ui/overlay";
import { Cards } from "./ui/cards";
import { EyeTiles } from "./ui/eyeTiles";
import { HeatBar, type HeatItem } from "./ui/heatbar";
import { PopoutDeck, Speaker, type Anchor } from "./ui/popouts";
import { MultiScreenDirector } from "./ui/multiscreen";
import { loadIntel } from "./vision/intel";
import { loadFacePills } from "./ui/facePills";
import { enableSensors } from "./sensors";
import "./ui/popouts.css";
import { cropBBoxToBlob, maybeAnalyse } from "./api/lietome";
import { DRAW_CONFIG } from "./config";
import { GazeTelemetry } from "./draw/gazeTelemetry";
import type { DrawMode } from "./draw/drawMode";
import type { BBox, TrackedFace } from "./types";

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
};

const video = $<HTMLVideoElement>("camera");
const canvas = $<HTMLCanvasElement>("overlay");
const stage = $("stage");
const watcherCount = $("watcher-count");
const fpsEl = $("fps");
const statusEl = $("status");
const startGate = $<HTMLButtonElement>("start-gate");
const zoomControl = $<HTMLLabelElement>("zoom-control");
const zoomInput = $<HTMLInputElement>("zoom");

const camera = new Camera();
const engine = new VisionEngine();
const tracker = new Tracker();
const overlay = new Overlay(canvas);
const cards = new Cards($("cards"));
const eyeTiles = new EyeTiles($("eyes"));
const heatBar = new HeatBar($("heat"));
const speaker = new Speaker();
const popouts = new PopoutDeck($("popouts"), speaker);
const muteBtn = $<HTMLButtonElement>("mute");
muteBtn.addEventListener("click", () => {
  const on = speaker.toggle();
  muteBtn.classList.toggle("on", on);
  muteBtn.textContent = on ? "🔊 voice" : "🔇 muted";
});

// Multiscreen demo: an agentic director that reopens the intel popouts as real
// windows and spreads them across the user's monitors. The click supplies the
// user gesture pop-ups + the Window Management API both require. Also reachable
// via ?demo=1 (armed to fire on the first interaction) or the "M" key.
const director = new MultiScreenDirector(speaker);
const demoBtn = document.createElement("button");
demoBtn.id = "demo";
demoBtn.type = "button";
demoBtn.title = "spread this app across your screens";
demoBtn.textContent = "⊞ screens";
document.querySelector(".hud-right")?.prepend(demoBtn);
function runDemo(): void {
  demoBtn.classList.toggle("on", !director.active);
  void director.run();
}
demoBtn.addEventListener("click", runDemo);
window.addEventListener("keydown", (e) => {
  if (e.key === "m" || e.key === "M") runDemo();
});
if (new URLSearchParams(location.search).has("demo")) {
  const arm = (): void => { window.removeEventListener("pointerdown", arm); runDemo(); };
  window.addEventListener("pointerdown", arm, { once: true });
}

// Auto-hunt: if no face is seen for a while, flip to the next camera to look for
// one. Reset the timer on any manual flip so we do not fight the user.
let lastFaceMs = 0;
let flipping = false;
const AUTO_FLIP_MS = 4500;

async function doFlip(): Promise<void> {
  if (flipping) return;
  flipping = true;
  try {
    const facing = await camera.flip();
    setupZoom();
    setStatus(`camera: ${facing === "user" ? "front" : "back"}`);
  } catch (err) {
    console.warn("[flip] failed:", err);
  } finally {
    lastFaceMs = 0;
    flipping = false;
  }
}

const flipBtn = $<HTMLButtonElement>("flip");
flipBtn.addEventListener("click", () => void doFlip());

// Draw Mode: a 2.5 minute reflective drawing ritual that reuses this camera,
// the shared PopoutDeck, and the shared Speaker. The DrawMode module (and its
// MediaPipe HandLandmarker) is loaded lazily on the first toggle so the Watch
// path never pays for it up front. While Draw Mode is active the gaze render
// loop is paused (running = false) so only HandPen touches the shared <video>,
// avoiding detectForVideo timestamp contention; the camera stream stays live.
const drawBtn = $<HTMLButtonElement>("mode-draw");
let drawMode: DrawMode | null = null;
let enteringDraw = false;
// Coexist: the Watch loop keeps running while Draw Mode is active (drawActive),
// switching to a light pass that still watches + feeds the gaze telemetry.
let drawActive = false;
let lastDrawExitAt = 0;
const gazeTelemetry = new GazeTelemetry();

// Walk-up invite: shown when someone lingers in view (config DRAW_CONFIG.walkup).
const drawInvite = document.createElement("button");
drawInvite.id = "draw-invite";
drawInvite.type = "button";
drawInvite.hidden = true;
drawInvite.innerHTML =
  '<span class="di-eye">✎</span>' +
  '<span class="di-text">Draw with me</span>' +
  '<span class="di-sub">a 2.5 minute reflection, tap to begin</span>';
drawInvite.addEventListener("click", () => void enterDrawMode());
stage.appendChild(drawInvite);

/** Nearest tracked watcher (the drawer), or null. Feeds the gaze telemetry. */
function primaryWatcher(ws: readonly TrackedFace[]): TrackedFace | null {
  let best: TrackedFace | null = null;
  for (const w of ws) {
    if (!best) {
      best = w;
      continue;
    }
    const bd = best.distanceM > 0 ? best.distanceM : Infinity;
    const wd = w.distanceM > 0 ? w.distanceM : Infinity;
    if (wd < bd) best = w;
  }
  return best;
}

function setInvite(on: boolean): void {
  if (on) {
    drawInvite.hidden = false;
    drawInvite.classList.add("on");
  } else {
    drawInvite.classList.remove("on");
    drawInvite.hidden = true;
  }
}

/** Show the walk-up invite when a near face has lingered, outside the cooldown. */
function maybeInvite(ws: readonly TrackedFace[], now: number): void {
  if (drawMode || enteringDraw) {
    setInvite(false);
    return;
  }
  const wk = DRAW_CONFIG.walkup;
  if (now - lastDrawExitAt < wk.cooldownMs) {
    setInvite(false);
    return;
  }
  const cand = ws.some(
    (w) =>
      w.distanceM > 0 &&
      w.distanceM <= wk.maxDistanceM &&
      now - w.lockedSince >= wk.minLockedMs,
  );
  setInvite(cand);
}

async function enterDrawMode(): Promise<void> {
  if (drawMode || enteringDraw) return;
  enteringDraw = true;
  drawBtn.classList.add("on");
  drawBtn.textContent = "✏ drawing";

  // Draw Mode needs a live feed for the hand input. If the camera has not come
  // up yet, bring it up now (same gesture path as the Watch start).
  if (video.readyState < 2 || video.videoWidth === 0) {
    try {
      setStatus("starting camera…");
      await camera.start(video);
      startGate.hidden = true;
      setupZoom();
    } catch (err) {
      console.error("[draw] camera failed:", err);
      setStatus(`⚠ camera: ${errMsg(err)}`);
      enteringDraw = false;
      exitDrawMode();
      return;
    }
  }

  // Coexist: keep the Watch loop RUNNING so the webcam keeps watching from the
  // PiP and feeds gaze telemetry; the loop switches to a light pass below.
  drawActive = true;
  setInvite(false);
  stage.classList.add("stage--draw");
  // Speech is gesture-gated; the toggle click is a valid user gesture.
  speaker.enable();

  try {
    const { DrawMode } = await import("./draw/drawMode");
    drawMode = new DrawMode({
      video,
      popouts,
      speaker,
      mount: stage,
      config: DRAW_CONFIG,
      gaze: gazeTelemetry,
      onExit: exitDrawMode,
    });
    setStatus("draw mode");
    await drawMode.start();
  } catch (err) {
    console.error("[draw] failed to start:", err);
    setStatus(`⚠ draw: ${errMsg(err)}`);
    exitDrawMode();
  } finally {
    enteringDraw = false;
  }
}

function exitDrawMode(): void {
  // Tear down the DrawMode instance (idempotent). We null our handle BEFORE
  // calling stop() because DrawMode.stop() itself calls deps.onExit (this
  // function); nulling first makes that re-entry a cheap no-op instead of a
  // loop.
  if (drawMode) {
    const dm = drawMode;
    drawMode = null;
    try {
      dm.stop();
    } catch (err) {
      console.warn("[draw] stop failed:", err);
    }
  }
  drawBtn.classList.remove("on");
  drawBtn.textContent = "✏ draw";
  drawActive = false;
  lastDrawExitAt = performance.now();
  stage.classList.remove("stage--draw");
  // The Watch loop was never paused in coexist mode; ensure it is running.
  if (!running) {
    running = true;
    requestAnimationFrame(loop);
  }
}

function toggleDrawMode(): void {
  if (drawMode) exitDrawMode();
  else void enterDrawMode();
}

drawBtn.addEventListener("click", toggleDrawMode);

// Animals are detected by a second engine (MediaPipe ObjectDetector). It is
// optional: if its model fails to load, the human path keeps working.
const animalEngine = new AnimalEngine();
const animalTracker = new AnimalTracker();

let running = false;
let enginePromise: Promise<void> | null = null;
let animalEnginePromise: Promise<void> | null = null;
let animalReady = false;

function setStatus(msg: string): void {
  statusEl.textContent = msg;
}

function fitCanvas(): void {
  overlay.resize(window.innerWidth, window.innerHeight);
}
window.addEventListener("resize", fitCanvas);

/** Load the vision model once; safe to call repeatedly. Retries after a failure. */
function ensureEngine(): Promise<void> {
  if (!enginePromise) {
    enginePromise = engine.init().catch((err) => {
      enginePromise = null; // allow a later tap to retry a fresh init
      throw err;
    });
  }
  return enginePromise;
}

/** Load the animal detector once. Non-fatal: failure just disables animals. */
function ensureAnimalEngine(): Promise<void> {
  if (!animalEnginePromise) {
    animalEnginePromise = animalEngine
      .init()
      .then(() => {
        animalReady = true;
      })
      .catch((err) => {
        animalEnginePromise = null;
        throw err;
      });
  }
  return animalEnginePromise;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

/** Bring the gate back with a big, visible error (not just the small status line). */
function showGateError(title: string, detail: string): void {
  startGate.hidden = false;
  startGate.disabled = false;
  const t = startGate.querySelector<HTMLElement>(".gate-text");
  const s = startGate.querySelector<HTMLElement>(".gate-sub");
  if (t) t.textContent = title;
  if (s) s.textContent = detail;
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms),
    ),
  ]);
}

/**
 * Entry point. ZERO-CLICK: we try this automatically on load, and again on the
 * first user interaction of any kind (move, key, touch, scroll, focus). The
 * camera needs a user-activation gesture the first time (browser rule), so a
 * silent on-load attempt succeeds only once permission was granted before; the
 * first-interaction attempt covers the initial grant without a deliberate click.
 * `silent` suppresses the error UI for the speculative on-load attempt so a
 * not-yet-granted camera just leaves us armed and waiting, not showing an error.
 * CAMERA FIRST: the live feed appears the instant the camera opens; the model
 * then loads in the background so a slow model never blocks the feed.
 */
async function startEverything(opts: { silent?: boolean } = {}): Promise<void> {
  if (running) return;
  startGate.disabled = true;

  // 1. Camera first.
  try {
    setStatus("starting camera…");
    await camera.start(video);
  } catch (err) {
    if (opts.silent) {
      // Expected on a first visit (no gesture yet): stay armed, no scary error.
      startGate.disabled = false;
      setStatus("move, tap, or focus to start");
      return;
    }
    console.error("[start] camera failed:", err);
    setStatus(`⚠ camera: ${errMsg(err)}`);
    showGateError("Camera failed, tap to retry", errMsg(err));
    return;
  }

  // 2. Show the feed immediately.
  startGate.hidden = true;
  setupZoom();
  // The tap is a user gesture, so unlock speech + sensors here and load intel.
  speaker.enable();
  void enableSensors();
  void loadIntel();
  void loadFacePills();
  running = true;
  requestAnimationFrame(loop);

  // 3. Load the models in the background; the loop guards on engine readiness.
  setStatus("loading vision model…");
  withTimeout(ensureEngine(), 45000, "model load")
    .then(() => setStatus(`live · ${engine.delegate}`))
    .catch((err) => {
      console.error("[start] model failed:", err);
      setStatus(`⚠ model: ${errMsg(err)}, camera only`);
    });

  // Animals + novelty heat factors load alongside, but never block the face path.
  void loadHeatFactors();
  withTimeout(ensureAnimalEngine(), 45000, "animal model load").catch((err) => {
    console.warn("[start] animal model failed (people still work):", err);
  });
}

function setupZoom(): void {
  const cap = camera.zoomCapability();
  if (!cap) {
    zoomControl.hidden = true;
    return;
  }
  zoomControl.hidden = false;
  zoomInput.value = "0";
  zoomInput.addEventListener("input", () => {
    void camera.setZoomLinear(parseFloat(zoomInput.value));
  });
}

const getCrop = (bbox: BBox): Promise<Blob | null> =>
  cropBBoxToBlob(video, video.videoWidth, video.videoHeight, bbox);

let lastFpsStamp = 0;
let frames = 0;
const DIAG = new URLSearchParams(location.search).has("diag");

function loop(now: number): void {
  if (!running) return;

  if (video.readyState >= 2 && video.videoWidth > 0) {
    const observations = engine.detect(video, now);
    tracker.update(observations, now);

    // Auto-flip-until-a-face: note when we last saw anyone; if a camera goes
    // face-blind for a while and another camera exists, flip to hunt for one.
    if (lastFaceMs === 0) lastFaceMs = now;
    if (observations.length > 0 || animalTracker.visible.length > 0) lastFaceMs = now;

    const faceWatchers = tracker.watchers;

    if (drawActive) {
      // Light pass while drawing: keep the PiP boxes live and feed the gaze
      // telemetry from the nearest watcher; skip the heavy Watch UI + popouts.
      overlay.render(video, tracker.visible, animalTracker.visible);
      watcherCount.textContent = String(faceWatchers.length);
      gazeTelemetry.sample(primaryWatcher(faceWatchers), now);
    } else {
      if (!flipping && camera.cameraCount !== 1 && now - lastFaceMs > AUTO_FLIP_MS) {
        void doFlip();
      }

      // Animals run on a second engine (optional; empty until its model loads).
      const animalObs = animalReady ? animalEngine.detect(video, now) : [];
      animalTracker.update(animalObs, now);

      overlay.render(video, tracker.visible, animalTracker.visible);

      const animalWatchers = animalTracker.watchers;
      // Everything making eye contact counts as "watching you": people + mammals.
      watcherCount.textContent = String(faceWatchers.length + animalWatchers.length);

      // People: analysis cards (lietome) + a magnified eye tile with gaze geometry.
      cards.sync(faceWatchers);
      eyeTiles.sync(faceWatchers, video, estimateGeometry);
      for (const w of faceWatchers) void maybeAnalyse(w, getCrop, now);

      // Animals only: the novelty heat-score bars (never applied to people).
      const heatItems: HeatItem[] = animalWatchers.map((a) => ({
        id: a.id,
        species: a.species,
        score: scoreHeat({
          id: a.id,
          species: a.species,
          confidence: a.confidence,
          headFacing: a.headFacing,
          bbox: a.bbox,
          motion: a.motion,
        }),
      }));
      heatBar.sync(heatItems);

      // Mission-Impossible intel popouts (spoken aloud) anchored near whoever is
      // locked on, cycling the gaze-science, fringe, and deep-research findings.
      const anchors: Anchor[] = [...faceWatchers, ...animalWatchers].map((w) => ({ x: w.cx, y: w.cy }));
      popouts.pulse(anchors, now);

      // Walk-up: invite a lingering, nearby person to draw.
      if (DRAW_CONFIG.walkup.enabled) maybeInvite(faceWatchers, now);
    }

    if (DIAG && !drawActive) {
      const o = observations[0];
      (window as unknown as { __eyes?: unknown }).__eyes = {
        faces: observations.length,
        visible: tracker.visible.length,
        watchers: faceWatchers.length,
        animals: animalTracker.watchers.length,
        distanceM: o ? +o.distanceM.toFixed(2) : null,
        actions: (faceWatchers[0]?.actions ?? []).map((a) => a.name),
        eyeTiles: document.getElementById("eyes")?.childElementCount ?? 0,
        popouts: document.getElementById("popouts")?.childElementCount ?? 0,
        score: o
          ? {
              headFacing: +o.headFacing.toFixed(3),
              gazeCentered: +o.gazeCentered.toFixed(3),
              eyeOpen: +o.eyeOpen.toFixed(3),
              lockScore: +o.lockScore.toFixed(3),
              yaw: +o.yaw.toFixed(1),
              pitch: +o.pitch.toFixed(1),
            }
          : null,
      };
    }
  }

  frames++;
  if (now - lastFpsStamp >= 500) {
    const fps = Math.round((frames * 1000) / (now - lastFpsStamp));
    fpsEl.textContent = `${fps} fps`;
    frames = 0;
    lastFpsStamp = now;
  }

  requestAnimationFrame(loop);
}

// Zero-click auto-start: begin on the FIRST user interaction of any kind, so
// there is no button to hunt for. mousemove/focus alone are not a camera-grant
// gesture on a first visit, but pointerdown/keydown/touch/wheel are, and once
// permission is granted the on-load attempt below starts it with no interaction
// at all on every future visit.
const AUTO_EVENTS = [
  "pointerdown", "pointermove", "mousemove", "touchstart",
  "keydown", "wheel", "scroll", "focus", "visibilitychange",
] as const;

function armAutoStart(): void {
  const trigger = (): void => {
    for (const ev of AUTO_EVENTS) window.removeEventListener(ev, trigger, true);
    void startEverything();
  };
  for (const ev of AUTO_EVENTS) {
    window.addEventListener(ev, trigger, { passive: true, capture: true });
  }
}

// The gate remains as a visible affordance and a retry target, but any
// interaction anywhere also starts things.
startGate.addEventListener("click", () => void startEverything());

// Service worker intentionally DISABLED during testing: a stale SW cache is a
// common cause of "still broken after a fix". Actively remove any previously
// installed worker + caches so every load is fresh. (Re-enable for production.)
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => void r.unregister()));
}
if (typeof caches !== "undefined") {
  caches.keys().then((keys) => keys.forEach((k) => void caches.delete(k)));
}

// Warm up the model, arm zero-click auto-start, and immediately try to start
// (works with no interaction once the camera permission has been granted once).
fitCanvas();
startGate.hidden = false;
setStatus("starting…");
ensureEngine()
  .then(() => setStatus("ready, move, tap, or focus to start"))
  .catch((err) => setStatus(`⚠ model load failed: ${err instanceof Error ? err.message : err}`));
armAutoStart();
void startEverything({ silent: true });
// Retry the silent start once the tab regains focus/visibility (e.g. returning
// to the pocketed phone), in case permission was granted in the meantime.
window.addEventListener("focus", () => void startEverything({ silent: true }));
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) void startEverything({ silent: true });
});
